import React, { useEffect, useRef } from 'react';
import Otto from './Otto';
import OttoAlive from './OttoAlive';
import useOttoPerch from './useOttoPerch';
import './otto.css';

// A per-page Otto "briefing bar" — surfaces the single top lifecycle item
// for a page (the page owns the priority-ladder logic and passes the
// resolved single state in). Two visual modes:
//   cockpit    — forest-green #004733 panel + Everyday Otto. For overdue /
//                attention / info / calm states; the dot colour carries the
//                severity (red = genuine negative, teal = info, green =
//                gentle/positive). CTA + × dismiss.
//   celebrate  — light-green card + Captain Otto + a small brand-palette
//                confetti burst. For win moments (e.g. Oak Status). Static
//                under prefers-reduced-motion (no confetti). Captain's navy
//                uniform needs the light card to read.
// Admin/internal only. Uses the shared dressed Otto symbols (build #7).
const prefersReducedMotion = () =>
  typeof window !== 'undefined' && window.matchMedia &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

// Brand-only confetti (build #9/#10 cross-cutting decision).
const CONFETTI = ['#92D400', '#009159', '#2E8FA1', '#B4E2A5'];

const DOT_HEX = { red: '#E0290A', teal: '#2E8FA1', green: '#92D400', amber: '#E89A2E', grey: '#9BA08C' };

function CelebrateBar({ eyebrow, title, body, ctaLabel, onCta, onDismiss }) {
  const wrapRef = useRef(null);
  useEffect(() => {
    // Reduced motion → static card, no confetti (brief guardrail).
    if (prefersReducedMotion() || !wrapRef.current) return undefined;
    const wrap = wrapRef.current;
    const originX = 44;   // over Otto's raised wings, left of the copy
    const originY = 20;
    const nodes = [];
    for (let i = 0; i < 22; i++) {
      const c = document.createElement('div');
      c.className = 'otto-pagebar-confetti';
      c.style.background = CONFETTI[i % CONFETTI.length];
      if (i % 3 === 0) c.style.borderRadius = '50%';
      else if (i % 3 === 1) { c.style.width = '5px'; c.style.height = '9px'; }
      c.style.left = `${originX}px`;
      c.style.top = `${originY}px`;
      wrap.appendChild(c);
      nodes.push(c);
      const ang = (-100 + Math.random() * 80) * Math.PI / 180;
      const vel = 50 + Math.random() * 70;
      const dx = Math.cos(ang) * vel;
      const dy = Math.sin(ang) * vel;
      const rot = Math.random() * 620 - 310;
      c.animate([
        { transform: 'translate(0,0) rotate(0)', opacity: 1 },
        { transform: `translate(${dx * 0.5}px,${dy}px) rotate(${rot * 0.5}deg)`, opacity: 1, offset: 0.4 },
        { transform: `translate(${dx}px,${70 + Math.random() * 30}px) rotate(${rot}deg)`, opacity: 0 },
      ], { duration: 1100 + Math.random() * 500, easing: 'cubic-bezier(.2,.6,.5,1)', fill: 'forwards' });
    }
    const t = setTimeout(() => nodes.forEach(n => n.remove()), 1800);
    return () => { clearTimeout(t); nodes.forEach(n => n.remove()); };
  }, []);

  return (
    <div
      ref={wrapRef}
      className="relative overflow-hidden bg-fusion-green-light/40 border border-fusion-emerald/30 rounded-card shadow-card p-5 flex items-center gap-4 mb-6">
      <div className="flex-shrink-0">
        <Otto pose="celebrating" variant="captain" size={68} ariaLabel="Otto celebrating" />
      </div>
      <div className="min-w-0 flex-1">
        {eyebrow && (
          <p className="font-['Metropolis'] text-[10px] font-bold tracking-[0.2em] uppercase text-fusion-emerald mb-1">{eyebrow}</p>
        )}
        <h3 className="font-['Metropolis'] text-[15px] font-extrabold text-ink-primary">{title}</h3>
        {body && <p className="font-['Metropolis'] text-[13px] text-ink-secondary leading-relaxed mt-1 max-w-[70ch]">{body}</p>}
      </div>
      {ctaLabel && (
        <button
          type="button"
          onClick={onCta}
          className="flex-shrink-0 rounded-[10px] bg-fusion-green text-[#14210A] font-['Metropolis'] text-[13px] font-bold px-4 h-10 hover:brightness-95 transition shadow-[0_8px_18px_-8px_rgba(146,212,0,0.7)]">
          {ctaLabel}
        </button>
      )}
      {onDismiss && (
        <button
          type="button"
          onClick={onDismiss}
          aria-label="Dismiss"
          className="absolute right-3 top-3 text-ink-tertiary hover:text-ink-primary text-lg leading-none bg-transparent border-0 cursor-pointer">×</button>
      )}
    </div>
  );
}

export default function OttoPageBar({
  mode = 'cockpit',
  eyebrow,
  title,
  body,
  dot,            // 'red' | 'teal' | 'green' | null — cockpit attention dot
  ctaLabel,
  onCta,
  onDismiss,
  segments,       // optional [{ dot, title, body, ctaLabel, onCta }] — several
                  // counts in ONE band (e.g. at-risk + overdue). Each is a line
                  // with its own dot and link; used instead of title/cta.
}) {
  // He is one owl: while the roaming overlay has him, this perch is empty.
  // Declared before the celebrate branch so the hook order never changes.
  const perch = useOttoPerch();
  if (mode === 'celebrate') {
    return <CelebrateBar eyebrow={eyebrow} title={title} body={body} ctaLabel={ctaLabel} onCta={onCta} onDismiss={onDismiss} />;
  }
  const dotHex = dot ? DOT_HEX[dot] : null;
  // Otto fronts this page-level briefing bar in every mode — it's his home
  // on the page, not a work queue in the sense the placement rule guards
  // against (the tidy-up's "remove Otto from a populated queue" applies to
  // an in-list queue banner, not this always-present page briefing band).
  const hasSegments = segments && segments.length > 0;
  return (
    <div className="relative bg-fusion-forest border border-white/[0.08] rounded-card shadow-card p-5 flex items-center gap-4 mb-6">
      {/* His perch. Fixed box so the band doesn't reflow while he's out
          roaming — see useOttoPerch. */}
      {/* 88px and vertically centred, matching the dashboard briefing bar
          exactly (Rob, 2026-08-14). These bars used a 60px Otto, so the same
          owl was two different sizes depending on which page you were on. */}
      <div className="flex-shrink-0" style={{ width: 88, height: 88 }}>
        {!perch.hidden && (
          <div className={perch.className}>
            {/* The SAME Otto as the dashboard briefing bar (Rob, 2026-08-14:
                "make sure the otto within the dashboards across all the pages
                matches the otto on the dashboard — same options for animations,
                eyes moving etc").
                This was the static Otto, which is a drawing: no cursor-follow,
                no blink, no click reactions, no seasonal accessory — and no
                wings, so the fly-in and fly-out had nothing to beat and he
                arrived board-stiff. OttoAlive is the interactive one and has
                always had all of it.
                `ejectable` is deliberately NOT passed: the once-a-session
                ejector treat belongs to his home on the dashboard, and firing
                it from four other bars would spend it before you got there. */}
            <OttoAlive size={88} />
          </div>
        )}
      </div>
      {hasSegments ? (
        <div className="min-w-0 flex-1 flex flex-col gap-2.5 pr-6">
          {segments.map((s, i) => {
            const sDot = s.dot ? DOT_HEX[s.dot] : null;
            return (
              <div key={i} className="flex items-baseline gap-2 flex-wrap">
                {sDot && <span aria-hidden="true" className="w-2 h-2 rounded-full flex-shrink-0 self-center" style={{ background: sDot }} />}
                <span className="font-['Metropolis'] text-[14px] font-extrabold text-white">{s.title}</span>
                {s.body && <span className="font-['Metropolis'] text-[12.5px] text-sb-text">{s.body}</span>}
                {s.ctaLabel && (
                  <button
                    type="button"
                    onClick={s.onCta}
                    className="font-['Metropolis'] text-[12.5px] font-bold text-fusion-green-light hover:text-white underline underline-offset-2 bg-transparent border-0 cursor-pointer">
                    {s.ctaLabel}
                  </button>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        <>
          <div className="min-w-0 flex-1 pr-6">
            {eyebrow && (
              <p className="font-['Metropolis'] text-[10px] font-bold tracking-[0.2em] uppercase text-fusion-green-light mb-1">{eyebrow}</p>
            )}
            <h3 className="font-['Metropolis'] text-[15px] font-extrabold text-white flex items-center gap-2">
              {dotHex && <span aria-hidden="true" className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: dotHex }} />}
              {title}
            </h3>
            {/* The CTA is an inline underlined link, NOT a lime block button
                (Rob, 2026-08-14). The segmented bar on Briefs has always read
                this way; the single-line bar on Suppliers had a solid lime
                button pinned to the right, so the same component looked like
                two different components depending on which page you were on.
                It also over-weighted the action: a lime block is the loudest
                control in the product, and "worth a chase" is not the loudest
                thing on the page.
                Sits with the body rather than pinned right, so it reads as the
                end of the sentence it belongs to. */}
            {(body || ctaLabel) && (
              <p className="font-['Metropolis'] text-[13px] text-sb-text leading-relaxed mt-1 max-w-[70ch]">
                {body}
                {ctaLabel && (
                  <>
                    {body ? ' ' : ''}
                    <button
                      type="button"
                      onClick={onCta}
                      className="font-['Metropolis'] text-[13px] font-bold text-fusion-green-light hover:text-white underline underline-offset-2 bg-transparent border-0 cursor-pointer p-0 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-fusion-green-light">
                      {ctaLabel}
                    </button>
                  </>
                )}
              </p>
            )}
          </div>
        </>
      )}
      {onDismiss && (
        <button
          type="button"
          onClick={onDismiss}
          aria-label="Dismiss"
          className="absolute right-3 top-3 text-sb-muted hover:text-white text-lg leading-none bg-transparent border-0 cursor-pointer">×</button>
      )}
    </div>
  );
}
